import api from '../core/api.js';
import router from '../core/router.js';
import stateManager from '../core/state.js';

const ENTITY_ROUTES = {
  project: (id) => `/projects/${id}`,
  task: (id) => `/tasks/${id}`,
  account: (id) => `/accounts/${id}`,
  contact: (id) => `/contacts/${id}`,
  invoice: (id) => `/invoices/${id}`,
  deal: (id) => `/deals/${id}`
};

// localStorage keys for the system-notification preference.
// 'enabled'  — user opted in AND browser granted permission. Fire system notifications.
// 'disabled' — user declined. Never fire.
// (missing)  — never asked; show the inline opt-in prompt on next dropdown open.
const SYS_NOTIF_PREF_KEY = 'pm_system_notif_pref';

class NotificationDropdown {
  constructor() {
    this.btn = document.getElementById('notification-btn');
    this.badge = document.getElementById('notification-badge');
    this.dropdown = document.getElementById('notification-dropdown');
    this.pollInterval = null;

    // Track notification IDs we've already shown a system toast for, so we
    // never double-fire on repeated polls. Seeded from the FIRST poll so
    // existing notifications don't all blast the desktop at app load.
    this._seenIds = new Set();
    this._seededSeenIds = false;

    if (!this.btn || !this.dropdown) return;

    this._bindEvents();
    this.refreshUnreadCount();
    this._startPolling();

    // Pause polling while the tab is hidden/backgrounded — an idle tab left
    // open all day would otherwise keep hitting the API every 30s for no
    // benefit (the user can't see the badge). Across many users those
    // background polls add up to real, unnecessary server load. On return to
    // the foreground we refresh immediately so the badge is never stale.
    this._onVisibilityChange = () => {
      if (document.hidden) {
        this._stopPolling();
      } else {
        this.refreshUnreadCount();
        this._startPolling();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  // Poll every 30 seconds. Idempotent — clears any existing timer first so
  // repeated foreground transitions never stack multiple intervals.
  _startPolling() {
    this._stopPolling();
    if (document.hidden) return;
    this.pollInterval = setInterval(() => this.refreshUnreadCount(), 30000);
  }

  _stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  // ─── System-notification preference helpers ─────────────────

  _getSysNotifPref() {
    try { return localStorage.getItem(SYS_NOTIF_PREF_KEY); } catch { return null; }
  }
  _setSysNotifPref(v) {
    try { localStorage.setItem(SYS_NOTIF_PREF_KEY, v); } catch { /* ignore */ }
  }

  // True if everything aligns to fire desktop notifications: user opted in,
  // browser supports it, and the user hasn't revoked permission since.
  _canFireSystemNotif() {
    if (typeof Notification === 'undefined') return false;
    if (this._getSysNotifPref() !== 'enabled') return false;
    return Notification.permission === 'granted';
  }

  // Browser API is async (returns a promise in modern browsers) but also
  // historically accepted a callback. We always treat as a promise.
  async _requestSystemNotifPermission() {
    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    try {
      const result = await Notification.requestPermission();
      return result; // 'granted' | 'denied' | 'default'
    } catch {
      return 'denied';
    }
  }

  _fireSystemNotif(n) {
    if (!this._canFireSystemNotif()) return;
    const senderName = n.sender ? `${n.sender.firstName || ''} ${n.sender.lastName || ''}`.trim() : 'Notification';
    try {
      const toast = new Notification(senderName || 'Notification', {
        body: n.message || '',
        tag: n.id, // dedupe at the OS level if user has multiple tabs open
      });
      toast.onclick = () => {
        try { window.focus(); } catch { /* ignore */ }
        const routeFn = ENTITY_ROUTES[n.entityType];
        if (routeFn && n.entityId) router.navigate(routeFn(n.entityId));
        toast.close();
      };
    } catch { /* permission revoked between check and use — ignore */ }
  }

  _bindEvents() {
    this.btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !this.dropdown.classList.contains('hidden');
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    });

    document.addEventListener('click', (e) => {
      if (!this.dropdown.contains(e.target) && e.target !== this.btn) {
        this.close();
      }
    });

    // Escape closes the dropdown and returns focus to the trigger,
    // so keyboard users aren't stranded.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.dropdown.classList.contains('hidden')) {
        this.close();
        this.btn?.focus();
      }
    });
  }

  async refreshUnreadCount() {
    if (!stateManager.get('token')) return;
    try {
      const res = await api.get('/notifications/unread-count');
      const count = res.count || 0;
      if (this.badge) {
        if (count > 0) {
          this.badge.classList.remove('hidden');
          this.badge.textContent = count > 99 ? '99+' : count;
        } else {
          this.badge.classList.add('hidden');
        }
      }

      // System-notification fan-out. Only fetch the list when the user has
      // opted in — keeps the network footprint identical to before for
      // users who haven't enabled desktop alerts.
      if (this._canFireSystemNotif()) {
        await this._pollAndFireSystemNotifs();
      }
    } catch (e) { /* ignore */ }
  }

  async _pollAndFireSystemNotifs() {
    try {
      const res = await api.get('/notifications?limit=10');
      const items = res.data || [];

      // First successful poll just seeds the seen-set so we don't dump a
      // batch of stale notifications onto the desktop right after login.
      if (!this._seededSeenIds) {
        for (const n of items) this._seenIds.add(n.id);
        this._seededSeenIds = true;
        return;
      }

      // Fire newest-last so the most recent notification ends up on top
      // of the OS notification stack.
      for (const n of [...items].reverse()) {
        if (this._seenIds.has(n.id)) continue;
        this._seenIds.add(n.id);
        if (!n.read) this._fireSystemNotif(n);
      }

      // Bound memory: keep at most the last 200 IDs seen.
      if (this._seenIds.size > 200) {
        const arr = Array.from(this._seenIds);
        this._seenIds = new Set(arr.slice(-200));
      }
    } catch { /* ignore poll errors */ }
  }

  async open() {
    this.dropdown.classList.remove('hidden');
    this.btn?.setAttribute('aria-expanded', 'true');
    this.dropdown.innerHTML = `
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span class="text-sm font-semibold text-gray-800">Notifications</span>
        <button id="mark-all-read-btn" class="text-xs text-blue-600 hover:underline">Mark all as read</button>
      </div>
      <div id="system-notif-banner"></div>
      <div id="notification-list" class="overflow-y-auto max-h-80">
        <div class="flex items-center justify-center py-6 text-gray-400 text-sm">
          <i class="fas fa-spinner fa-spin mr-2"></i> Loading...
        </div>
      </div>
    `;

    document.getElementById('mark-all-read-btn')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      await this._markAllRead();
    });

    this._renderSystemNotifBanner();
    await this._loadNotifications();
  }

  // Renders one of three states in the dropdown header banner area:
  //   - never asked  → ask to enable desktop notifications
  //   - enabled      → green confirmation + disable link
  //   - disabled / browser-denied → small re-enable link
  _renderSystemNotifBanner() {
    const banner = document.getElementById('system-notif-banner');
    if (!banner) return;
    if (typeof Notification === 'undefined') {
      banner.innerHTML = '';
      return;
    }

    const pref = this._getSysNotifPref();
    const perm = Notification.permission;

    // Hide banner once user has answered AND browser state matches.
    if (pref === 'enabled' && perm === 'granted') {
      banner.innerHTML = `
        <div class="px-4 py-2 bg-green-50 border-b border-green-100 flex items-center justify-between text-xs">
          <span class="text-green-700"><i class="fas fa-check-circle mr-1"></i>Desktop alerts on</span>
          <button id="sysnotif-disable" class="text-gray-500 hover:text-gray-700 hover:underline">Turn off</button>
        </div>`;
      document.getElementById('sysnotif-disable')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._setSysNotifPref('disabled');
        this._renderSystemNotifBanner();
      });
      return;
    }

    if (pref === 'disabled' || perm === 'denied') {
      const note = perm === 'denied'
        ? 'Desktop alerts blocked in browser settings.'
        : 'Desktop alerts off.';
      banner.innerHTML = `
        <div class="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between text-xs">
          <span class="text-gray-600"><i class="fas fa-bell-slash mr-1"></i>${note}</span>
          ${perm !== 'denied' ? '<button id="sysnotif-enable-small" class="text-blue-600 hover:underline">Enable</button>' : ''}
        </div>`;
      document.getElementById('sysnotif-enable-small')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._askEnableSystemNotifs();
      });
      return;
    }

    // pref === null (never asked) and browser permission is 'default'.
    banner.innerHTML = `
      <div class="px-4 py-3 bg-blue-50 border-b border-blue-100 text-xs">
        <p class="text-gray-700 mb-2">
          <i class="fas fa-desktop text-blue-600 mr-1"></i>
          Show new notifications on your desktop too?
        </p>
        <div class="flex items-center gap-2">
          <button id="sysnotif-enable" class="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700">
            Yes, enable
          </button>
          <button id="sysnotif-decline" class="px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded">
            Not now
          </button>
        </div>
      </div>`;

    document.getElementById('sysnotif-enable')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      await this._askEnableSystemNotifs();
    });
    document.getElementById('sysnotif-decline')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this._setSysNotifPref('disabled');
      this._renderSystemNotifBanner();
    });
  }

  async _askEnableSystemNotifs() {
    const result = await this._requestSystemNotifPermission();
    if (result === 'granted') {
      this._setSysNotifPref('enabled');
      // Reset seen-set seeding so the first opt-in poll captures current
      // state without firing a barrage for everything already on the list.
      this._seenIds = new Set();
      this._seededSeenIds = false;
    } else {
      // denied / dismissed / unsupported — record as disabled so we don't
      // re-prompt every time the dropdown opens.
      this._setSysNotifPref('disabled');
    }
    this._renderSystemNotifBanner();
  }

  close() {
    this.dropdown.classList.add('hidden');
    this.btn?.setAttribute('aria-expanded', 'false');
  }

  async _loadNotifications() {
    try {
      const res = await api.get('/notifications?limit=15');
      const notifications = res.data || [];
      const list = document.getElementById('notification-list');
      if (!list) return;

      if (notifications.length === 0) {
        list.innerHTML = `
          <div class="flex flex-col items-center justify-center py-8 text-gray-400">
            <i class="fas fa-bell-slash text-2xl mb-2"></i>
            <span class="text-sm">No notifications</span>
          </div>`;
        return;
      }

      list.innerHTML = notifications.map(n => this._renderItem(n)).join('');

      list.querySelectorAll('.notif-item').forEach(el => {
        el.addEventListener('click', async () => {
          const id = el.dataset.id;
          const entityType = el.dataset.entityType;
          const entityId = el.dataset.entityId;

          if (!el.classList.contains('read')) {
            await api.put(`/notifications/${id}/read`, {}).catch(() => {});
            el.classList.add('read', 'bg-white');
            el.classList.remove('bg-blue-50');
            this.refreshUnreadCount();
          }

          const routeFn = ENTITY_ROUTES[entityType];
          if (routeFn) {
            this.close();
            router.navigate(routeFn(entityId));
          }
        });
      });
    } catch (e) {
      const list = document.getElementById('notification-list');
      if (list) list.innerHTML = `<div class="text-sm text-red-500 px-4 py-3">Failed to load notifications.</div>`;
    }
  }

  _renderItem(n) {
    const senderName = n.sender ? `${n.sender.firstName} ${n.sender.lastName}` : 'Someone';
    const timeAgo = this._timeAgo(n.createdAt);
    const isUnread = !n.read;
    const bgClass = isUnread ? 'bg-blue-50' : 'bg-white';
    const readClass = isUnread ? '' : 'read';

    return `
      <div class="notif-item ${bgClass} ${readClass} px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
           data-id="${n.id}"
           data-entity-type="${n.entityType}"
           data-entity-id="${n.entityId}">
        <div class="flex items-start gap-3">
          <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
            ${senderName.charAt(0).toUpperCase()}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-xs text-gray-700 leading-snug">${this._escapeHtml(n.message)}</p>
            <p class="text-xs text-gray-400 mt-1">${timeAgo}</p>
          </div>
          ${isUnread ? '<span class="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></span>' : ''}
        </div>
      </div>`;
  }

  async _markAllRead() {
    try {
      await api.put('/notifications/read-all', {});
      this.refreshUnreadCount();
      // Re-render list with all read
      const list = document.getElementById('notification-list');
      if (list) {
        list.querySelectorAll('.notif-item').forEach(el => {
          el.classList.add('read', 'bg-white');
          el.classList.remove('bg-blue-50');
          el.querySelector('span.bg-blue-500')?.remove();
        });
      }
    } catch (e) { /* ignore */ }
  }

  _timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  destroy() {
    this._stopPolling();
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }
  }
}

export default NotificationDropdown;
